export const parsePointsFromPointRange = (pointRange = '') => {
  if (typeof pointRange === 'number') {
    return Number.isFinite(pointRange) ? pointRange : 0;
  }

  const match = String(pointRange).match(/(-?\d+(?:\.\d+)?)/);
  if (!match) return 0;
  return Number.parseFloat(match[1]) || 0;
};

export const getValidPointRange = ({
  criterion = {},
  levelKey,
  rubricLevels = []
} = {}) => {
  if (!levelKey || !rubricLevels || rubricLevels.length === 0) {
    return { min: 0, max: 0 };
  }

  // Calculate base points for the selected level
  const basePoints = calculateLevelPoints({ criterion, levelKey, rubricLevels, customPoints: null });

  // Find the index of the selected level
  const currentLevelIndex = rubricLevels.findIndex((level) => level.level === levelKey);

  if (currentLevelIndex === -1) {
    return { min: basePoints, max: basePoints };
  }

  // Check if this is the highest level
  if (currentLevelIndex === rubricLevels.length - 1) {
    // For the highest level, use the criterion's max points as the upper limit
    const maxPoints = typeof criterion?.maxPoints === 'string'
      ? parseFloat(criterion.maxPoints)
      : Number(criterion?.maxPoints) || 0;
    return { min: basePoints, max: maxPoints };
  }

  // Get the next level's base points
  const nextLevel = rubricLevels[currentLevelIndex + 1];
  const nextLevelPoints = calculateLevelPoints({
    criterion,
    levelKey: nextLevel.level,
    rubricLevels,
    customPoints: null
  });

  // Max is one less than the next level's base points
  return { min: basePoints, max: nextLevelPoints - 1 };
};

export const calculateLevelPoints = ({
  criterion = {},
  levelKey,
  rubricLevels = [],
  customPoints = null
} = {}) => {
  // If custom points are provided and valid, use them
  if (customPoints !== null && customPoints !== undefined && Number.isFinite(Number(customPoints))) {
    return Number(customPoints);
  }

  const levelDef = levelKey ? criterion?.levels?.[levelKey] : null;
  const hasExplicitPointRange = (typeof levelDef?.pointRange === 'string' && levelDef.pointRange.trim() !== '')
    || typeof levelDef?.pointRange === 'number';

  if (hasExplicitPointRange) {
    const parsed = parsePointsFromPointRange(levelDef.pointRange);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  const rubricLevel = rubricLevels.find((level) => level.level === levelKey);
  const maxPoints = typeof criterion?.maxPoints === 'string'
    ? parseFloat(criterion.maxPoints)
    : Number(criterion?.maxPoints) || 0;

  if (rubricLevel && typeof rubricLevel.multiplier === 'number') {
    const derivedPoints = maxPoints * rubricLevel.multiplier;
    return Math.round(derivedPoints);
  }

  return 0;
};

export const calculateRubricFinalScore = ({
  criteria = [],
  rubricLevels = [],
  rubricGrading = {},
  rubricTotalPoints = 100,
  getLevelsForCriterion
} = {}) => {
  let totalEarnedPoints = 0;
  let totalMaxPoints = 0;

  const criteriaResults = criteria.map((criterion = {}) => {
    const maxPoints = typeof criterion.maxPoints === 'string'
      ? parseFloat(criterion.maxPoints)
      : Number(criterion.maxPoints) || 0;

    const grading = rubricGrading?.[criterion.id];
    const selectedLevelKey = grading?.selectedLevel;
    const customPoints = grading?.customPoints;
    const levelsForCriterion = typeof getLevelsForCriterion === 'function'
      ? getLevelsForCriterion(criterion)
      : rubricLevels;

    const earnedPoints = calculateLevelPoints({
      criterion,
      levelKey: selectedLevelKey,
      rubricLevels: levelsForCriterion,
      customPoints
    });

    totalEarnedPoints += earnedPoints;
    totalMaxPoints += Number.isFinite(maxPoints) ? maxPoints : 0;

    const criterionPercent = maxPoints > 0 ? (earnedPoints / maxPoints) * 100 : 0;

    return {
      criterionId: criterion.id,
      earnedPoints,
      maxPoints,
      criterionPercent
    };
  });

  const effectiveMaxPoints = totalMaxPoints || Number(rubricTotalPoints) || 0;
  const rawPercentage = effectiveMaxPoints > 0
    ? (totalEarnedPoints / effectiveMaxPoints) * 100
    : 0;
  const finalPercentage = Math.round(rawPercentage);
  const finalPoints = totalEarnedPoints;

  return {
    finalPercentage,
    finalPoints,
    rawPercentage,
    totalEarnedPoints,
    totalMaxPoints: effectiveMaxPoints,
    criteriaResults
  };
};
